/** Overlay scene that renders all in-run HUD elements (health, stamina, dash, skill slots, vignette, event crawl). */
import Phaser from 'phaser';
import type { GameScene } from './GameScene';
import { VignetteOverlay } from '../ui/VignetteOverlay';
import { AttunementPanel } from '../ui/AttunementPanel';
import { CombatHUD } from '../ui/CombatHUD';
import { SkillSlotsHUD, SlotState } from '../ui/SkillSlotsHUD';
import { SkillIconRegistry } from '../ui/SkillIconRegistry';
import { CrawlOverlay } from '../ui/CrawlOverlay';
import { setupCrispCamera } from '../ui/CrispUtil';
import { SaveSystem } from '../systems/SaveSystem';
import { ConfigLoader } from '../systems/ConfigLoader';

/**
 * Runs in parallel with GameScene as a launched overlay. Subscribes to GameScene events
 * and delegates each stat update to the appropriate HUD component.
 */
export class UIScene extends Phaser.Scene {
  private vignette!: VignetteOverlay;
  private attunementPanel!: AttunementPanel;
  private combatHUD!: CombatHUD;
  private skillSlotsHUD!: SkillSlotsHUD;
  private crawl!: CrawlOverlay;
  private crawlEnabled = true;
  private gameScene: Phaser.Scene | null = null;

  constructor() {
    super({ key: 'UIScene' });
  }

  /** Instantiates all HUD components, subscribes to GameScene events, and seeds initial state. */
  create(): void {
    setupCrispCamera(this);
    SkillIconRegistry.init(ConfigLoader.v4Trees);
    this.vignette = new VignetteOverlay(this);
    this.attunementPanel = new AttunementPanel(this);
    this.combatHUD = new CombatHUD(this);
    this.skillSlotsHUD = new SkillSlotsHUD(this);
    this.crawl = new CrawlOverlay(this);

    // Load event log visibility from persisted settings
    const settings = new SaveSystem().loadSettings();
    this.crawlEnabled = settings.eventLogEnabled;

    this.gameScene = this.scene.get('GameScene');
    if (!this.gameScene) {
      console.warn('UIScene: GameScene not found, HUD events disabled');
      return;
    }

    this.gameScene.events.on('player-health-changed', this.onHealthChanged, this);
    this.gameScene.events.on('player-stamina-changed', this.onStaminaChanged, this);
    this.gameScene.events.on('player-dash-changed', this.onDashChanged, this);
    this.gameScene.events.on('tree-selection-changed', this.onTreeSelectionChanged, this);
    this.gameScene.events.on('player-skill-slots', this.onSkillSlots, this);
    this.gameScene.events.on('player-death-linger', this.onDeathLinger, this);
    this.gameScene.events.on('player-respawned', this.onPlayerRespawned, this);
    this.gameScene.events.on('death-overlay-in', this.onDeathOverlayIn, this);
    this.gameScene.events.on('death-overlay-out', this.onDeathOverlayOut, this);
    this.gameScene.events.on('crawl-push', this.onCrawlPush, this);

    // React to event log toggle from PauseScene (fires on the game-level emitter)
    this.game.events.on('event-log-toggled', this.onEventLogToggled, this);

    // Seed attunement panel with current selection (events only fire on change)
    const gs = this.gameScene as GameScene;
    this.attunementPanel.setSelectedTrees([...gs.player.attunement.getSelectedTrees()]);

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
  }

  /** Ticks animated HUD components each frame (vignette pulse, combat bars, skill slots, event crawl). */
  update(_time: number, delta: number): void {
    this.vignette.update(delta);
    this.combatHUD.draw();
    this.skillSlotsHUD.draw();
    if (this.crawlEnabled) {
      this.crawl.draw();
    } else {
      this.crawl.clear();
    }
  }

  /** Forwards a health update to the vignette (red edge glow) and combat HUD bar. */
  private onHealthChanged(current: number, max: number): void {
    this.vignette.setHealthRatio(current / max);
    this.combatHUD.setHealth(current, max);
  }

  /** Forwards a stamina update to the combat HUD bar. */
  private onStaminaChanged(current: number, max: number): void {
    this.combatHUD.setStamina(current, max);
  }

  /** Forwards dash charge state and refill progress to the combat HUD. */
  private onDashChanged(charges: number, max: number, refillProgress: number): void {
    this.combatHUD.setDashCharges(charges, max, refillProgress);
  }

  /** Updates the attunement panel whenever the player's tree selection changes. */
  private onTreeSelectionChanged(data: { selectedTrees: string[] }): void {
    this.attunementPanel.setSelectedTrees(data.selectedTrees);
  }

  /** Triggers the vignette's death-collapse animation for the given linger duration. */
  private onDeathLinger(durationMs: number): void {
    this.vignette.startDeathCollapse(durationMs);
  }

  /** Resets the vignette to its neutral state after the player respawns. */
  private onPlayerRespawned(): void {
    this.vignette.reset();
  }

  /**
   * Fades this scene's camera to black in sync with the GameScene death overlay,
   * ensuring UIScene elements (health bars, skill slots) disappear together with the darkness.
   */
  private onDeathOverlayIn({ fadeInMs, ease }: { fadeInMs: number; ease: string }): void {
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: fadeInMs,
      ease,
    });
  }

  /** Fades this scene's camera back in once the death sequence finishes and the world is ready. */
  private onDeathOverlayOut({ fadeOutMs, ease }: { fadeOutMs: number; ease: string }): void {
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: fadeOutMs,
      ease,
    });
  }

  /** Forwards a crawl event from GameScene into the CrawlOverlay. */
  private onCrawlPush(data: { text: string; color: string }): void {
    this.crawl.push(data.text, data.color);
  }

  /** Toggles crawl visibility when the player flips the setting in PauseScene. */
  private onEventLogToggled(enabled: boolean): void {
    this.crawlEnabled = enabled;
    if (!enabled) this.crawl.clear();
  }

  /** Pushes the full hotbar slot array to the skill-slots HUD for display. */
  private onSkillSlots(slots: SlotState[]): void {
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      this.skillSlotsHUD.setSlot(i, s);
    }
  }

  /** Unsubscribes all GameScene event listeners and destroys HUD components on scene shutdown. */
  private cleanup(): void {
    if (this.gameScene) {
      this.gameScene.events.off('player-health-changed', this.onHealthChanged, this);
      this.gameScene.events.off('player-stamina-changed', this.onStaminaChanged, this);
      this.gameScene.events.off('player-dash-changed', this.onDashChanged, this);
      this.gameScene.events.off('tree-selection-changed', this.onTreeSelectionChanged, this);
      this.gameScene.events.off('player-skill-slots', this.onSkillSlots, this);
      this.gameScene.events.off('player-death-linger', this.onDeathLinger, this);
      this.gameScene.events.off('player-respawned', this.onPlayerRespawned, this);
      this.gameScene.events.off('death-overlay-in', this.onDeathOverlayIn, this);
      this.gameScene.events.off('death-overlay-out', this.onDeathOverlayOut, this);
      this.gameScene.events.off('crawl-push', this.onCrawlPush, this);
    }
    this.game.events.off('event-log-toggled', this.onEventLogToggled, this);
    this.vignette.destroy();
    this.combatHUD.destroy();
    this.skillSlotsHUD.destroy();
    this.crawl.destroy();
  }
}
